import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../Common/Loading';

// --- Configuration ---
const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const STATE_KEY = 'EL_ListOfTasks_State';

// --- Types ---
interface CustomerData {
  login?: string;
  surname?: string;
  email?: string;
  phone?: string;
  state?: string;
  utc?: string;

  schoolCode?: string;
  familyCode?: string;
  address?: string;
  actionDate?: string;
  typeOfIssue?: string;
  reason?: string;
  activityStatus?: string;

  distributor?: string;
  student?: string;
  yearLevel?: string;
  city?: string;
  postCode?: string;
  dateOfLastActivity?: string;
  dateOfSale?: string;
  expiryDate?: string;
  sourceIndicator?: string;

  [key: string]: any;
}

interface DropdownItem {
  value: string;
  text: string;
}

interface FiltersType {
  login: string;
  surname: string;
  student: string;
  email: string;
  phone: string;
  suburb: string;
  year: string;
  postCode: string;
  state: string;
  purchaseOrder: string;
  category: string;
  user: string;
  view: string;
  searchCompleted: boolean;
  activeUsers: boolean;
  notOnVacation: boolean;
  fromDate: string;
  toDate: string;
  lastAccessFrom: string;
  lastAccessTo: string;
  expiryFrom: string;
  expiryTo: string;
  distributor: string;
}

const DEFAULT_FILTERS: FiltersType = {
  login: '',
  surname: '',
  student: '',
  email: '',
  phone: '',
  suburb: '',
  year: '-Select-',
  postCode: '',
  state: '',
  purchaseOrder: '',
  category: '',
  user: '',
  view: 'Mine',
  searchCompleted: false,
  activeUsers: false,
  notOnVacation: false,
  fromDate: '',
  toDate: '',
  lastAccessFrom: '',
  lastAccessTo: '',
  expiryFrom: '',
  expiryTo: '',
  distributor: ''
};

// --- Styles ---
const styles: { [key: string]: React.CSSProperties } = {
  cssBody: {
    backgroundColor: '#E8E8FF',
    fontFamily: 'Times New Roman',
    fontSize: '9pt',
    height: "100vh",
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box'
  },
  cssTableBackground: {
    paddingTop: '5px',
    paddingBottom: '5px',
    border: '2px solid white',
    backgroundColor: '#E8E8FF',
    flexShrink: 0
  },
  cssLabel: {
    fontFamily: 'Arial',
    fontSize: '9pt',
    color: 'black',
    textAlign: 'left',
    verticalAlign: 'middle'
  },
  cssTextBox: {
    fontFamily: 'Arial',
    fontSize: '9pt',
    paddingLeft: '2px',
    border: '1px solid #7f9db9',
  },
  cssButton: {
    fontFamily: 'Arial',
    fontSize: '9pt',
    padding: '2px',
    cursor: 'pointer',
    backgroundColor: '#f0f0f0',
    border: '1px solid #707070',
    width: '50px',
    textAlign: 'center'
  },
  cssDropDownList: {
    fontFamily: 'Arial',
    fontSize: '9pt',
  },
  gridHeader: {
    backgroundColor: '#c7ddfd',
    color: '#2859b3',
    fontWeight: 'normal',
    paddingLeft: '10px',
    textAlign: 'left',
    fontFamily: 'Arial',
    fontSize: '12pt',
    border: "1px solid #80a0ff",
    height: "35px",
    whiteSpace: 'nowrap'
  },
  gridCell: {
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRight: '1px solid #a3c0e8',
    fontFamily: 'Arial',
    fontSize: '12pt',
    whiteSpace: 'nowrap'
  },
  entryHeaderCell: {
    textAlign: 'left',
    paddingRight: '3px',
    width: '80px',
    borderRight: 'solid 2px white',
    verticalAlign: 'middle',
    fontFamily: 'Times New Roman',
    fontSize: '12pt',
    whiteSpace: 'nowrap'
  },
  footer: {
    flexShrink: 0,
    marginTop: "auto",
    zIndex: 100,
    backgroundColor: "rgb(199 221 253)",
    padding: "10px",
    borderTop: "2px solid white",
    border: "2px solid #80a0ff",
    fontFamily: "Arial",
    fontSize: "12pt",
    color: "#2859ac",
    fontWeight: "bold"
  },
};

// --- Session restore helpers ---
// IMPORTANT: this is read ONCE, synchronously, before React ever paints.
// Doing the restore via a useEffect (the old approach) created a race:
// the "auto persist" effect could run in the SAME commit right after the
// restore effect, before the restored setState calls had actually applied,
// and would overwrite sessionStorage with the still-default values.
// Reading it up-front like this and feeding it into useState's lazy
// initializer removes that race completely - there is no "default state"
// frame that ever gets rendered or persisted when a saved state exists.
interface PersistedState {
  filterType?: 'CustView' | 'CustEntry';
  filters?: FiltersType;
  selectedCountry?: string;
  dateMode?: 'current' | 'future' | 'custom';
  showLastAccess?: boolean;
  showExpiry?: boolean;
  results?: CustomerData[];
  currentPage?: number;
  rowsPerPage?: number;
  forceEntryMode?: boolean;
  hasSearched?: boolean;
}

function readSavedState(): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function ELListOfTasks() {
  const shouldRestore =
    sessionStorage.getItem("EL_ListOfTasks_Restore") === "1";

  if (!shouldRestore) {
    sessionStorage.removeItem(STATE_KEY);
  }
  // Read once, synchronously. Stable for the lifetime of this component
  // instance (its setter is never called).
  const [savedState] = useState<PersistedState | null>(() =>
    shouldRestore ? readSavedState() : null
  );

  const [defaultFromDate] = useState<string>(() => {
    const today = new Date();
    const fromDateObj = new Date(today);
    fromDateObj.setMonth(fromDateObj.getMonth() - 1);
    return fromDateObj.toISOString().slice(0, 10);
  });
  const [defaultToDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  // --- State (all lazily initialized from savedState where relevant) ---
  const [filterType, setFilterType] = useState<'CustView' | 'CustEntry'>(
    () => savedState?.filterType || 'CustView'
  );
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CustomerData[]>(() => savedState?.results || []);
  const [users, setUsers] = useState<DropdownItem[]>([]);
  const [distributors, setDistributors] = useState<DropdownItem[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>(() => savedState?.selectedCountry || '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [dateMode, setDateMode] = useState<'current' | 'future' | 'custom'>(
    () => savedState?.dateMode || 'current'
  );
  const [showDateRange, setShowDateRange] = useState(() => (savedState?.dateMode || 'current') === 'custom');
  const [showLastAccess, setShowLastAccess] = useState(() => !!savedState?.showLastAccess);
  const [showExpiry, setShowExpiry] = useState(() => !!savedState?.showExpiry);
  const [countries, setCountries] = useState<string[]>([]);
  const [issueTypes, setIssueTypes] = useState<DropdownItem[]>([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState(() => !!savedState?.hasSearched);
  const [rowsPerPage, setRowsPerPage] = useState(() => savedState?.rowsPerPage ?? 20);
  const [currentPage, setCurrentPage] = useState(() => savedState?.currentPage ?? 1);
  const [forceEntryMode, setForceEntryMode] = useState(() => !!savedState?.forceEntryMode);
  const [showStudentYearColumns, setShowStudentYearColumns] = useState(() =>
    !!(
      savedState?.filters?.student?.trim() ||
      (
        savedState?.filters?.year &&
        savedState.filters.year !== "-Select-"
      )
    )
  );
  const navigate = useNavigate();
  const skipAutoSearch = useRef(true);
  const skipActiveVacationAutoSearch = useRef(true);

  const [filters, setFilters] = useState<FiltersType>(() => {
    if (savedState?.filters) return savedState.filters;
    return {
      ...DEFAULT_FILTERS,
      lastAccessFrom: defaultFromDate,
      lastAccessTo: defaultToDate,
      expiryFrom: defaultFromDate,
      expiryTo: defaultToDate
    };
  });

  const showEntryFilters = filterType === "CustEntry";
  const showEntryGrid = filterType === "CustEntry" || forceEntryMode;

  const showStudentCol =
    filterType === "CustEntry" &&
    showStudentYearColumns;

  const showYearCol =
    filterType === "CustEntry" &&
    showStudentYearColumns;

  const getTextColor = (status: string | undefined) => {
    switch (status) {
      case 'P':
        return 'grey';
      case 'C':
        return 'red';
      default:
        return 'inherit';
    }
  };

  // --- Persistence helpers ---
  const persistState = (overrides: Partial<{
    filterType: string; filters: FiltersType; selectedCountry: string;
    dateMode: string; showLastAccess: boolean; showExpiry: boolean;
    results: CustomerData[]; currentPage: number; rowsPerPage: number;
    forceEntryMode: boolean; hasSearched: boolean;
  }> = {}) => {
    try {
      const snapshot = {
        filterType,
        filters,
        selectedCountry,
        dateMode,
        showLastAccess,
        showExpiry,
        results,
        currentPage,
        rowsPerPage,
        forceEntryMode,
        hasSearched,
        ...overrides
      };
      sessionStorage.setItem(STATE_KEY, JSON.stringify(snapshot));
    } catch (e) {
      // ignore storage errors (e.g. private browsing quota)
    }
  };

  // --- Effects ---
  useEffect(() => {

    fetchDropdowns();
    fetchCountries();
    fetchIssueTypes();

    // Only honor the URL's FilterType on a genuinely fresh visit (no saved
    // state) - once we have a saved state, that's the source of truth so a
    // stale FilterType in the URL can't clobber what the user was doing.
    const params = new URLSearchParams(window.location.search);
    const urlFilterType = params.get('FilterType');
    if (urlFilterType && !savedState) {
      if (urlFilterType.toLowerCase() === 'entry' || urlFilterType.toLowerCase() === 'custentry') {
        setFilterType('CustEntry');
      } else if (urlFilterType.toLowerCase() === 'view' || urlFilterType.toLowerCase() === 'custview') {
        setFilterType('CustView');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep sessionStorage continuously in sync with whatever the user has
  // typed/selected, so Student / Year / every other filter comes back
  // exactly as it was, whether the user presses the browser Back button or
  // the "Back to Menu" link - even if they never clicked GO before leaving.
  useEffect(() => {
    persistState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCountry, filterType, dateMode, showLastAccess, showExpiry, forceEntryMode]);

  useEffect(() => {
    sessionStorage.removeItem("EL_ListOfTasks_Restore");
  }, []);

  // Default country is Canada (falls back to first available country if
  // Canada isn't in the list). Skipped entirely if state was restored, so
  // we never stomp on a restored country selection.
  useEffect(() => {
    if (savedState) return;
    if (countries.length > 0 && !selectedCountry) {
      const Australia = countries.find(c => c.toUpperCase() === 'AUSTRALIA');
      setSelectedCountry(Australia || countries[0]);
    }
  }, [countries, selectedCountry]);

  useEffect(() => {
    setShowDateRange(dateMode === 'custom');
  }, [dateMode]);

  useEffect(() => {
    if (skipAutoSearch.current) {
      skipAutoSearch.current = false;
      return;
    }

    if (showLastAccess) {
      setFilters(prev => ({
        ...prev,
        lastAccessFrom: prev.lastAccessFrom || defaultFromDate,
        lastAccessTo: prev.lastAccessTo || defaultToDate
      }));
    }

    if (showExpiry) {
      setFilters(prev => ({
        ...prev,
        expiryFrom: prev.expiryFrom || defaultFromDate,
        expiryTo: prev.expiryTo || defaultToDate
      }));
    }
  }, [showLastAccess, showExpiry, filters.notOnVacation]);

  // Active Users / Not on Vacation always re-run the search when toggled
  // (mirrors the VB checkboxes' AutoPostBack="true"). We skip only the very
  // first render so restoring a previously-checked value doesn't trigger an
  // unwanted search on mount - after that, any change (by the user, or a
  // future restore) is treated the same way, which is more robust than
  // trying to track "was this a real click" with a ref that resets on every
  // remount.
  useEffect(() => {
    if (skipActiveVacationAutoSearch.current) {
      skipActiveVacationAutoSearch.current = false;
      return;
    }
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.activeUsers, filters.notOnVacation]);

  const fetchIssueTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}api/get_issue_types/`);
      if (!response.ok) return;
      const data = await response.json();
      setIssueTypes(data.issues || []);
    } catch (error) {
      console.error('Failed to fetch issue types', error);
    }
  };

  const fetchDistributors = async (country: string) => {
    const response = await fetch(
      `${API_BASE_URL}api/get_distributors_ENTRY/?country=${encodeURIComponent(country)}`
    );
    if (response.ok) {
      const data = await response.json();
      setDistributors(data.distributors || []);
    }
  };

  useEffect(() => {
    if (selectedCountry) {
      fetchDistributors(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchDropdowns = async () => {
    try {
      const [usersRes, distRes] = await Promise.all([
        fetch(`${API_BASE_URL}api/get_users/`),
        fetch(
          `${API_BASE_URL}api/get_distributors_ENTRY/?country=${encodeURIComponent(selectedCountry)}`
        )
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      if (distRes.ok) {
        const data = await distRes.json();
        setDistributors(data.distributors || []);
      }
    } catch (error) {
      console.error("Error fetching dropdowns", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}api/get_countries/`);
      const data = await response.json();

      const countryNames = Array.isArray(data.countries)
        ? [...data.countries].sort((a, b) => a.localeCompare(b))
        : [];

      setCountries(countryNames);
    } catch (error) {
      setErrorMessage('Failed to fetch countries. Please try again.');
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if ((name === "activeUsers" || name === "notOnVacation") && filterType === "CustView" && checked) {
      setForceEntryMode(true);
    }

    setFilters(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    if (name === "student" || name === "year") {
      setResults([]);
      setHasSearched(false);
    }
  };


  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
  };


  const handleSearch = async (overrides: { forceEntryMode?: boolean } = {}) => {
    const effectiveForceEntryMode =
      overrides.forceEntryMode !== undefined
        ? overrides.forceEntryMode
        : forceEntryMode;

    setHasSearched(true);
    setLoading(true);
    setResults([]);
    setShowStudentYearColumns(
      filterType === "CustEntry" &&
      (
        filters.student.trim() !== "" ||
        (filters.year !== "" && filters.year !== "-Select-")
      )
    );
    try {
      const payload = {
        filterType,
        country: selectedCountry,
        login: filters.login,
        surname: filters.surname,
        student: filters.student,
        email: filters.email,
        phone: filters.phone,
        suburb: filters.suburb,
        year: filters.year,
        postCode: filters.postCode,
        state: filters.state,
        distributor: filters.distributor,
        category: filters.category,
        user: filters.user,
        view: filters.view,
        dateRange: dateMode,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        searchCompleted: filters.searchCompleted,
        lastAccessOn: showLastAccess,
        lastAccessFrom: filters.lastAccessFrom,
        lastAccessTo: filters.lastAccessTo,
        expiryDate: showExpiry,
        expiryFrom: filters.expiryFrom,
        expiryTo: filters.expiryTo,
        activeUsers: filters.activeUsers,
        notOnVacation: filters.notOnVacation,
        forceEntryMode: effectiveForceEntryMode,
        supportLogin: sessionStorage.getItem("L") || "",
      };



      const response = await fetch(`${API_BASE_URL}api/get_customers/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();

        let newResults: CustomerData[] = data.customers || [];

        // Remove duplicate student rows when searching ONLY by Student
        if (
          filterType === "CustEntry" &&
          filters.student.trim() !== "" &&
          (!filters.year || filters.year === "-Select-")
        ) {
          const seen = new Set<string>();

          newResults = newResults.filter((row: CustomerData) => {
            const key = [
              row.schoolCode,
              row.familyCode,
              row.login,
              row.student?.trim().toUpperCase()
            ].join("|");

            if (seen.has(key)) {
              return false;
            }

            seen.add(key);
            return true;
          });
        }

        setResults(newResults);
        setCurrentPage(1);

        persistState({
          results: newResults,
          currentPage: 1,
          hasSearched: true,
          forceEntryMode: effectiveForceEntryMode,
        });
      } else {
        alert("Error fetching data");
      }
    } catch (error) {
      console.error(error);
      alert("Connection error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters({
      ...DEFAULT_FILTERS,
      lastAccessFrom: defaultFromDate,
      lastAccessTo: defaultToDate,
      expiryFrom: defaultFromDate,
      expiryTo: defaultToDate
    });
    setDateMode('current');
    setShowLastAccess(false);
    setShowExpiry(false);
    setResults([]);
    setHasSearched(false);
    setShowStudentYearColumns(false);
    setCurrentPage(1);
    setForceEntryMode(false);
    try {
      sessionStorage.removeItem(STATE_KEY);
    } catch (e) {
      // ignore
    }
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (direction: 'prev' | 'next' | 'first' | 'last') => {
    const totalPages = Math.ceil(results.length / rowsPerPage);
    switch (direction) {
      case 'prev':
        setCurrentPage(prev => Math.max(prev - 1, 1));
        break;
      case 'next':
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
        break;
      case 'first':
        setCurrentPage(1);
        break;
      case 'last':
        setCurrentPage(totalPages);
        break;
    }
  };

  const goToRecord = (row: CustomerData) => {
    persistState();
    sessionStorage.setItem("EL_ListOfTasks_Restore", "1");
    const params = new URLSearchParams();
    params.append('FilterType', showEntryGrid ? 'Entry' : 'View');
    params.append('FamilyCode', row.familyCode || '');
    params.append('SchoolCode', row.schoolCode || '');
    params.append('Login', row.login || '');
    params.append('S', 'A');
    params.append('F', 'AA');
    params.append('L', sessionStorage.getItem('L') || '');
    navigate(`/SUPPORT/EL_AddEditTask?${params.toString()}`);
    sessionStorage.setItem("AddEditLogin", row.login || '');
  };

  // Flush the very latest state to sessionStorage before navigating away,
  // so Student / Year / any other in-progress filter is exactly what's
  // restored when the user comes back to this page (whether via this link
  // or the browser Back button).
  const goToMenu = () => {
    sessionStorage.removeItem("EL_ListOfTasks_Restore");
    navigate("/SysAdmin");
  };

  // --- Render Sections ---
  const renderCustView = () => (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", borderBottom: "2px solid white", paddingBottom: "5px", alignItems: "center", marginBottom: '5px' }}>
        <div style={{ display: "flex", gap: "2%", alignItems: "center", justifyContent: "center", width: "100%" }}>
          <div style={{ color: "#0066CC", fontSize: "18px", fontWeight: "bold", fontFamily: "Trebuchet MS" }}>Customer Support - View</div>
          <div style={{ color: "#0066CC", fontSize: "18px", fontWeight: "bold", fontFamily: "Trebuchet MS" }}>
            Country
            <select
              value={selectedCountry}
              onChange={handleCountryChange}
              style={{ marginLeft: '5px', padding: '1px', border: '1px solid #8a8a8a', borderRadius: '3px' }}
            >
              {countries.map((country, index) => (
                <option key={index} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{
          color: "blue", fontSize: "12pt", fontWeight: "bold", textDecoration: "underline", cursor: "pointer", flexShrink: 0,
          paddingRight: "10px", fontFamily: "Arial"
        }} onClick={goToMenu}>Back to Menu</div>
      </div>

      <table id="tblView" border={0} cellPadding={0} cellSpacing={0} style={{ width: '100%', fontSize: '11pt', display: 'table' }}>
        <tbody>
          <tr style={{ paddingTop: '5px' }}>
            <td valign="middle" style={{ textAlign: 'left', paddingRight: '2px', width: '41%' }} className="cssLabel">
              <label style={{ fontSize: '11pt', fontFamily: "Times New Roman", marginRight: "8px" }}>
                <input type="radio" name="RadioA" checked={dateMode === 'current'} onChange={() => setDateMode('current')} style={{ fontFamily: "Times New Roman", margin: "0", verticalAlign: "top", marginTop: "2px" }} /> Current
              </label>&nbsp;
              <label style={{ fontSize: '11pt', fontFamily: "Times New Roman", marginRight: "8px" }}>
                <input type="radio" name="RadioA" checked={dateMode === 'future'} onChange={() => setDateMode('future')} style={{ fontFamily: "Times New Roman", margin: "0", verticalAlign: "top", marginTop: "2px" }} /> Future
              </label>&nbsp;
              <label style={{ fontSize: '11pt', fontFamily: "Times New Roman" }}>
                <input type="radio" name="RadioA" checked={dateMode === 'custom'} onChange={() => setDateMode('custom')} style={{ fontFamily: "Times New Roman", margin: "0", verticalAlign: "top", marginTop: "2px" }} /> Custom
              </label>&nbsp;&nbsp;
              <span id="sp_Date" style={{ display: showDateRange ? 'inline' : 'none', fontSize: '11pt' }}>
                From <input type="date" name="fromDate" value={filters.fromDate} onChange={handleInputChange} style={{ width: '120px' }} />
                &nbsp;To <input type="date" name="toDate" value={filters.toDate} onChange={handleInputChange} style={{ width: '120px' }} />
                &nbsp;<label><input type="checkbox" name="searchCompleted" checked={filters.searchCompleted} onChange={handleInputChange} /> Search Completed</label>
              </span>
            </td>

            <td valign="middle" style={{ textAlign: 'left', width: '20%', whiteSpace: 'nowrap', fontSize: '11pt', fontFamily: "Times New Roman" }} >
              Login&nbsp;
              <input type="text" name="login" value={filters.login} onChange={handleInputChange} className="cssTextBox" style={{ width: '120px', borderRadius: '2px' }} maxLength={50} />
              &nbsp;Category&nbsp;
              <select name="category" value={filters.category} onChange={handleInputChange} className="cssDropDownList" style={{ width: '115px', borderRadius: '2px' }}>
                <option value="">--Select--</option>
                {issueTypes.map(issue => <option key={issue.value} value={issue.value}>{issue.text}</option>)}
              </select>
            </td>
            <td valign="middle" style={{ textAlign: 'left', width: '21%', whiteSpace: 'nowrap', ...styles.cssLabel, fontSize: '11pt', fontFamily: "Arial" }}>
              User&nbsp;
              <select name="user" value={filters.user} onChange={handleInputChange} className="cssDropDownList" style={{ width: '160px' }}>
                <option value="">-- All --</option>
                {users.map(u => <option key={u.value} value={u.value}>{u.text}</option>)}
              </select>
            </td>
            <td style={{ display: 'none' }}><input type="checkbox" /> Future Dates</td>
            <td valign="middle" style={{ textAlign: 'left', width: '6%', whiteSpace: 'nowrap', ...styles.cssLabel, fontSize: '11pt', fontFamily: "Open Sans" }}>
              View&nbsp;
              <select name="view" value={filters.view} onChange={handleInputChange} className="cssDropDownList" style={{ width: '70px' }}>
                <option value="Mine">Mine</option>
                <option value="All">All</option>
              </select>
            </td>
            <td
              style={{
                position: 'relative',
                textAlign: 'center',
                width: '12%',
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                <button
                  className="cssButton"
                  style={{
                    width: '45px',
                    backgroundColor: '#f0f0f0',
                    color: 'black',
                    border: '1px solid black',
                    borderRadius: '2px',
                    fontSize: '12px',
                    fontFamily: 'arial',
                  }}
                  onClick={() => {
                    setForceEntryMode(false);
                    handleSearch({ forceEntryMode: false });
                  }}
                >
                  GO
                </button>

                &nbsp;

                <button
                  className="cssButton"
                  style={{
                    width: '45px',
                    backgroundColor: '#f0f0f0',
                    color: 'black',
                    border: '1px solid black',
                    borderRadius: '2px',
                    fontSize: '12px',
                    fontFamily: 'arial',
                  }}
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );

  const renderCustEntry = () => (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", borderBottom: "2px solid white", paddingBottom: "5px", alignItems: "center", marginBottom: '5px' }}>
        <div style={{ display: "flex", gap: "2%", alignItems: "center", justifyContent: "center", width: "100vw" }}>
          <div style={{ color: "#0066CC", fontSize: "18px", fontWeight: "bold", fontFamily: "Trebuchet MS" }}>Customer Support - Entry</div>
          <div style={{ color: "#0066CC", fontSize: "18px", fontWeight: "bold", fontFamily: "Trebuchet MS" }}>
            Country
            <select
              value={selectedCountry}
              onChange={handleCountryChange}
              style={{ marginLeft: '5px', padding: '1px', border: '1px solid #8a8a8a', borderRadius: '3px' }}
            >
              {countries.map((country, index) => (
                <option key={index} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{
          color: "blue", fontSize: "12pt", fontWeight: "bold", textDecoration: "underline", cursor: "pointer", flexShrink: 0,
          paddingRight: "10px", fontFamily: "Arial"
        }} onClick={goToMenu}>Back to Menu</div>
      </div>
      <table id="tblEntry" style={{ width: '100%', display: 'table' }} cellPadding={3} cellSpacing={0} border={0}>
        <tbody>
          <tr style={{ fontFamily: "Times New Roman" }}>
            <td style={styles.entryHeaderCell} className="cssLabel">Login</td>
            <td style={styles.entryHeaderCell} className="cssLabel">Surname</td>
            <td style={styles.entryHeaderCell} className="cssLabel">Student</td>
            <td style={styles.entryHeaderCell} className="cssLabel">Email</td>
            <td style={styles.entryHeaderCell} className="cssLabel">Phone</td>
            <td style={styles.entryHeaderCell} className="cssLabel">Suburb</td>
            <td style={styles.entryHeaderCell} className="cssLabel">Year</td>
            <td style={{ ...styles.entryHeaderCell, whiteSpace: 'nowrap' }} className="cssLabel">Post Code</td>
            <td style={{ ...styles.entryHeaderCell, whiteSpace: 'nowrap' }} className="cssLabel">State</td>
            <td style={{ ...styles.entryHeaderCell, width: '150px', whiteSpace: 'nowrap' }} className="cssLabel">Distributor</td>
            <td valign="middle" style={{ textAlign: 'center', width: '12%', whiteSpace: 'nowrap' }}>
              <button className="cssButton" style={{ width: '45px', backgroundColor: '#f0f0f0', color: 'black', border: "1px solid black", borderRadius: "2px", fontSize: "12px", fontFamily: 'arial' }} onClick={() => handleSearch()}>GO</button>
              &nbsp;
              <button className="cssButton" style={{ width: '45px', backgroundColor: '#f0f0f0', color: 'black', border: "1px solid black", borderRadius: "2px", fontSize: "12px", fontFamily: 'arial' }} onClick={handleReset}>Reset</button>
            </td>
          </tr>
          <tr >
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="login" value={filters.login} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={50} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="surname" value={filters.surname} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={50} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="student" value={filters.student} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={50} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="email" value={filters.email} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={50} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="phone" value={filters.phone} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={50} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="suburb" value={filters.suburb} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={50} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <select name="year" value={filters.year} onChange={handleInputChange} className="cssDropDownList" style={{ width: '80px' }}>
                <option value="-Select-">-Select-</option>
                {[...Array(17).keys()].map(i => <option key={i} value={i - 4}>{i - 4}</option>)}
              </select>
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="postCode" value={filters.postCode} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={50} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <input type="text" name="state" value={filters.state} onChange={handleInputChange} className="cssTextBox" style={{ width: '80px' }} maxLength={10} />
            </td>
            <td style={{ borderRight: 'solid 2px white' }}>
              <select name="distributor" value={filters.distributor} onChange={handleInputChange} style={{ width: '145px' }}>
                <option value="">All</option>
                {distributors.map(d => <option key={d.value} value={d.value}>{d.text}</option>)}
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );

  const renderAdvancedFilters = () => (
    <div id="divFilter_LastAccesson" style={{ ...styles.cssTableBackground, borderTop: "none" }}>
      <table style={{ width: '100%' }} cellPadding={0} cellSpacing={0}>
        <tbody>
          <tr>
            <td valign="middle" style={{ textAlign: 'left', height: '30px', paddingTop: '2px', paddingBottom: '2px', ...styles.cssLabel }}>
              <label style={{ fontSize: "12pt", fontFamily: "Times New Roman" }}>
                <input type="checkbox" checked={showLastAccess} onChange={(e) => setShowLastAccess(e.target.checked)} />
                Last access on
              </label>
              <span id="spn_LastAcessOn" style={{ display: showLastAccess ? 'inline' : 'none', fontSize: "11pt", fontFamily: "Arial" }}>
                &nbsp;between : &nbsp;
                From <input type="date" name="lastAccessFrom" value={filters.lastAccessFrom} onChange={handleInputChange} />
                &nbsp;To <input type="date" name="lastAccessTo" value={filters.lastAccessTo} onChange={handleInputChange} />
              </span>
              &nbsp;&nbsp;
              <label style={{ fontSize: "12pt", fontFamily: "Times New Roman" }}>
                <input type="checkbox" name="activeUsers" checked={filters.activeUsers} onChange={handleInputChange} /> Active users
              </label>
              &nbsp;&nbsp;
              <label style={{ fontSize: "12pt", fontFamily: "Times New Roman" }}>
                <input type="checkbox" checked={showExpiry} onChange={(e) => setShowExpiry(e.target.checked)} />
                Expiry Date
              </label>
              <span id="spn_ExpDate" style={{ display: showExpiry ? 'inline' : 'none', fontSize: "11pt", fontFamily: "Times New Roman" }}>
                &nbsp;between : &nbsp;
                From <input type="date" name="expiryFrom" value={filters.expiryFrom} onChange={handleInputChange} />
                &nbsp;To <input type="date" name="expiryTo" value={filters.expiryTo} onChange={handleInputChange} />
              </span>
              &nbsp;&nbsp;
              <label style={{ fontSize: "12pt", fontFamily: "Times New Roman" }}>
                <input type="checkbox" name="notOnVacation" checked={filters.notOnVacation} onChange={handleInputChange} /> Not on Vacation
              </label>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderGrid = () => {
    if (loading) return <Loading />;
    if (!hasSearched) return null;

    if (results.length === 0) {
      return (
        <div style={{ padding: '10px', fontFamily: 'Arial', fontSize: '10pt' }}>
          No records found.
        </div>
      );
    }

    const totalPages = Math.ceil(results.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, results.length);
    const currentResults = results.slice(startIndex, endIndex);

    return (
      <div
        style={{
          flex: 1,
          height: 0,
          overflowY: "auto",
          overflowX: "auto",
          border: "1px solid #80a0ff",
          width: "100%"
        }}
      >
        <table
          style={{
            width: '100%',
            // minWidth: '1600px',
            borderCollapse: 'collapse',
            border: '1px solid #80a0ff',
            tableLayout: 'auto'
          }}
        >
          <thead style={{ position: 'sticky', top: -1, backgroundColor: "#c7ddfd" }}>
            <tr style={{ backgroundColor: "#c7ddfd", position: 'sticky', top: -1 }}>
              {!showEntryGrid ? (
                <>
                  <th style={styles.gridHeader}>#</th>
                  {/* <th style={styles.gridHeader}>Login</th> */}
                  <th style={styles.gridHeader}>Surname</th>
                  {/* <th style={styles.gridHeader}>Address</th> */}
                  <th style={styles.gridHeader}>State</th>
                  <th style={styles.gridHeader}>UTC</th>
                  <th style={styles.gridHeader}>Phone</th>
                  <th style={styles.gridHeader}>Email</th>
                  <th style={styles.gridHeader}>Action Date</th>
                  <th style={styles.gridHeader}>Type Of Issue</th>
                  <th style={styles.gridHeader}>Reason</th>
                  <th style={styles.gridHeader}>Last Access On</th>
                  <th style={styles.gridHeader}>Expiry Date</th>
                </>
              ) : (
                <>
                  <th style={styles.gridHeader}>#</th>
                  <th style={styles.gridHeader}>*</th>
                  <th style={styles.gridHeader}>Distributor</th>
                  {/* <th style={styles.gridHeader}>Login</th> */}
                  <th style={styles.gridHeader}>Surname</th>
                  {showStudentCol && <th style={styles.gridHeader}>Student</th>}
                  {showYearCol && <th style={styles.gridHeader}>Year</th>}
                  {/* <th style={styles.gridHeader}>Address</th> */}
                  <th style={styles.gridHeader}>Suburb</th>
                  <th style={styles.gridHeader}>State</th>
                  <th style={styles.gridHeader}>UTC</th>
                  <th style={styles.gridHeader}>Phone</th>
                  <th style={styles.gridHeader}>Email</th>
                  <th style={styles.gridHeader}>Post Code</th>
                  <th style={styles.gridHeader}>Date of Sale</th>
                  <th style={styles.gridHeader}>Last Access On</th>
                  <th style={styles.gridHeader}>Expiry Date</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {currentResults.map((row, idx) => {
              const absoluteIndex = startIndex + idx;
              const isSelected = selectedRowIndex === absoluteIndex;

              return (
                <tr
                  key={idx}
                  style={{
                    backgroundColor: isSelected ? 'orange' : idx % 2 === 0 ? '#f4faff' : 'white',
                    cursor: 'pointer',
                    height: '35px',
                    color: getTextColor(row.activityStatus),
                  }}
                  onClick={() => {
                    setSelectedRowIndex(absoluteIndex);
                    goToRecord(row);
                  }}
                >
                  {!showEntryGrid ? (
                    <>
                      <td style={styles.gridCell}>{absoluteIndex + 1}</td>
                      {/* <td style={styles.gridCell}>{row.login}</td> */}
                      <td style={styles.gridCell}>{row.surname}</td>
                      {/* <td style={styles.gridCell}>{row.address}</td> */}
                      <td style={styles.gridCell}>{row.state}</td>
                      <td style={styles.gridCell}>{row.utc}</td>
                      <td style={styles.gridCell}>{row.phone}</td>
                      <td style={styles.gridCell}>{row.email}</td>
                      <td style={styles.gridCell}>{row.actionDate}</td>
                      <td style={styles.gridCell}>{row.typeOfIssue}</td>
                      <td style={styles.gridCell}>{row.reason}</td>
                      <td style={styles.gridCell}>{row.dateOfLastActivity}</td>
                      <td style={styles.gridCell}>{row.expiryDate}</td>
                    </>
                  ) : (
                    <>
                      <td style={styles.gridCell}>{absoluteIndex + 1}</td>
                      <td style={styles.gridCell}>{row.sourceIndicator || ''}</td>
                      <td style={styles.gridCell}>{row.distributor}</td>
                      {/* <td style={styles.gridCell}>{row.login}</td> */}
                      <td style={styles.gridCell}>{row.surname}</td>
                      {showStudentCol && <td style={styles.gridCell}>{row.student}</td>}
                      {showYearCol && <td style={styles.gridCell}>{row.yearLevel}</td>}
                      {/* <td style={styles.gridCell}>{row.address}</td> */}
                      <td style={styles.gridCell}>{row.city}</td>
                      <td style={styles.gridCell}>{row.state}</td>
                      <td style={styles.gridCell}>{row.utc}</td>
                      <td style={styles.gridCell}>{row.phone}</td>
                      <td style={styles.gridCell}>{row.email}</td>
                      <td style={styles.gridCell}>{row.postCode}</td>
                      <td style={styles.gridCell}>{row.dateOfSale}</td>
                      <td style={styles.gridCell}>{row.dateOfLastActivity}</td>
                      <td style={styles.gridCell}>{row.expiryDate}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFooter = () => {
    const totalPages = Math.ceil(results.length / rowsPerPage) || 1;

    return (
      <div
        style={{
          ...styles.footer,
          display: "flex",
          justifyContent: showEntryGrid ? "space-between" : "center",
          alignItems: "center",
        }}
      >
        {/* Show legend only in Entry */}
        {showEntryGrid && (
          <div style={{ fontSize: "12pt" }}>
            * O = Old YLA Sales entered, M = Migrated from old YLA,
            A = Registered from APP, B = Bootcamp,
            K = Kinetic Website
          </div>
        )}

        {/* Center section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <button
            onClick={() => handleSearch()}
            style={{
              color: "blue",
              fontWeight: "bold",
              cursor: "pointer",
              background: "none",
              border: "none",
              textDecoration: "underline",
              fontSize: "15px",
            }}
          >
            Refresh
          </button>

          {/* <span>Rows per page:</span> */}
          {/* 
          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            style={{
              fontFamily: "Arial",
              fontSize: "9pt",
            }}
          >
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select> */}

          {/* ADDED EDITABLE ROWS PER PAGE - 03-09-2026 */}
          <span>Rows per page:</span>

          <input
            type="number"
            min={1}
            value={rowsPerPage}
            onChange={(e) => {
              const value = Number(e.target.value);

              if (value >= 1) {
                setRowsPerPage(value);
                setCurrentPage(1);

                persistState({
                  rowsPerPage: value,
                  currentPage: 1,
                });
              }
            }}
            style={{
              width: "50px",
              fontFamily: "Arial",
              fontSize: "9pt",
              textAlign: "center",
            }}
          />

          <button
            onClick={() => handlePageChange("first")}
            style={{ color: "#2859ac", fontWeight: "bold" }}
          >
            &lt;&lt;
          </button>

          <button
            onClick={() => handlePageChange("prev")}
            style={{ color: "#2859ac", fontWeight: "bold" }}
          >
            &lt;
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange("next")}
            style={{ color: "#2859ac", fontWeight: "bold" }}
          >
            &gt;
          </button>

          <button
            onClick={() => handlePageChange("last")}
            style={{ color: "#2859ac", fontWeight: "bold" }}
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    );
  };
  return (
    <div style={styles.cssBody}>
      {/* Filter Container */}
      <div id="divFilter" style={styles.cssTableBackground}>
        {showEntryFilters ? renderCustEntry() : renderCustView()}
      </div>

      {/* Middle Advanced Filters */}
      {renderAdvancedFilters()}

      {/* Results - flex:1 so it fills remaining space and is the ONLY scroll container */}
      <div
        id="divResultDisplay"
        style={{
          flex: "1 1 0",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: "5px",
          minHeight: 0
        }}
      >
        {renderGrid()}
      </div>

      {/* Footer - pinned to bottom via flex layout above, includes Refresh */}
      {results.length > 0 && renderFooter()}
    </div>
  );
}

export default ELListOfTasks;